import { useCallback, useState } from 'react';

import { message } from 'antd';
import { useTranslation } from 'react-i18next';

import { useApolloClient } from '@apollo/client';

import { useCareerProfessionsQuery, useChooseGrowthTrackMutation } from 'core';

import { IProfession, transformProfessions } from './transform';

/**
 * ТЗ 0039, доводка UX — справочник профессий.
 *
 * `cache-and-network`, как у остальных экранов раздела: список меняется редко
 * (треки заводит методист), но «есть у меня» зависит от навыков студента и
 * обязано освежаться, когда он вернулся сюда после начисления за курс.
 *
 * 🔴 Выбор цели живёт ЗДЕСЬ, а не в `useGrowth`. Тот перечитывал документ
 * `MyGrowth`, который экран профессий больше не рисует, — рефетч промахнулся бы
 * молча (шрам ТЗ 0033).
 */
export const useProfessions = () => {
  const { t } = useTranslation();
  const client = useApolloClient();
  const { data, loading, refetch } = useCareerProfessionsQuery({
    fetchPolicy: 'cache-and-network',
  });
  const [choose] = useChooseGrowthTrackMutation({
    refetchQueries: ['CareerJourneyBoard'],
  });
  /**
   * Код карточки, по которой идёт мутация, а НЕ общий булев: с общим на время
   * запроса гасли кнопки всех шести карточек разом.
   */
  const [choosing, setChoosing] = useState<string | null>(null);

  const chooseTrack = useCallback(
    async (code: string) => {
      setChoosing(code);
      try {
        const { data: result } = await choose({ variables: { trackCode: code } });
        const ok = Boolean(result?.chooseGrowthTrack?.success);
        if (!ok) {
          // 🔴 Серверное `message` не показываем: оно по-русски, а интерфейс
          // польский. Код ошибки переводим сами.
          message.error(t('growth.professions.chooseFailed'));
          return false;
        }
        // Карта навыков кешируется по аргументу `trackCode`, и запись без
        // аргумента — отдельная от записи со своим кодом. После смены цели
        // устарели обе, поэтому выносим поле целиком.
        client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'myCareerSkillGraph' });
        client.cache.gc();
        await refetch();
        message.success(t('growth.professions.chooseDone'));
        return true;
      } finally {
        setChoosing(null);
      }
    },
    [choose, client, refetch, t],
  );

  const professions: IProfession[] = transformProfessions(data);
  return { professions, loading, chooseTrack, choosing };
};
